<script setup>
import { computed, ref } from 'vue';

/**
 * The Financial Breakdown Per Day, rebuilt.
 *
 * Same table as web/admin/businessSummary.php and the same month-at-a-time reading, but the
 * numbers are computed differently. On that page "FH" is a residual — method total minus Food
 * Manager minus Apex (businessSummary.php:379) — so anything it could not classify landed there.
 * Here every line is classified explicitly and a payment is apportioned across its order's lines
 * in proportion to their value, which is why the product-line columns reconcile to Net exactly.
 */
const props = defineProps({
  /** { days, totals, methods, lines } from the daily endpoint. */
  ledger: {
    type: Object,
    default: null,
  },
  /** True while a different month is being fetched; the table shown belongs to the previous one. */
  isLoading: {
    type: Boolean,
    default: false,
  },
});

/** The legacy payments screen, which takes a day as mm/dd/yyyy. */
const PAYMENTS_URL = '/office/payments.php';

const MEASURE = { NET: 'net', COLLECTED: 'collected', REFUNDED: 'refunded' };
const measure = ref(MEASURE.NET);
const measures = [
  { value: MEASURE.NET, label: 'Net' },
  { value: MEASURE.COLLECTED, label: 'Collected' },
  { value: MEASURE.REFUNDED, label: 'Refunded' },
];

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const compact = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});
const number = new Intl.NumberFormat('en-US');

const days = computed(() => props.ledger?.days ?? []);
const totals = computed(() => props.ledger?.totals ?? null);
const methods = computed(() => props.ledger?.methodColumns ?? []);
const lines = computed(() => props.ledger?.lines ?? []);

const hasUnallocated = computed(() => Math.abs(totals.value?.unallocated ?? 0) > 0.005);
const hasApplePay = computed(() => Math.abs(totals.value?.applePay ?? 0) > 0.005);

/**
 * One method's figure for a row, in the measure the reader picked.
 * @param {object} row A day or the totals.
 * @param {string} key Method key.
 * @returns {number}
 */
function methodValue(row, key) {
  return row.methodDetail?.[key]?.[measure.value] ?? 0;
}

/** The methods' combined total in the chosen measure, which the method shares are taken of. */
const methodTotal = computed(() => (totals.value
  ? methods.value.reduce((sum, method) => sum + methodValue(totals.value, method.key), 0)
  : 0));

/**
 * A column's share of a whole, to one decimal place.
 * @param {number} part The column's total.
 * @param {number} whole What the shares are taken of.
 * @returns {string} The percentage, or a dash when there is nothing to divide by.
 */
function share(part, whole) {
  if (!whole) {
    return '—';
  }
  // Adding zero turns a rounded -0 into 0 so a tiny negative never reads "-0.0%".
  return `${(Math.round((part / whole) * 1000) / 10 + 0).toFixed(1)}%`;
}

/**
 * Link to the legacy payments list for one day, as the old summary page has.
 *
 * That list filters on the date a payment was created; this table groups on the date it was
 * approved. They are the same day for card payments and can differ for cheques entered late.
 * @param {string} day Y-m-d.
 * @returns {string}
 */
function paymentsUrl(day) {
  const parts = day.split('-');
  const formatted = `${parts[1]}/${parts[2]}/${parts[0]}`;
  return `${PAYMENTS_URL}?createDateStart=${formatted}&createDateEnd=${formatted}`;
}

/**
 * Day label, with the weekday — the legacy table shows it and it is genuinely useful for
 * spotting the weekend trough.
 * @param {string} day Y-m-d.
 * @returns {string}
 */
function dayLabel(day) {
  const date = new Date(`${day}T00:00:00`);
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', weekday: 'short' });
}

/**
 * True for Saturday and Sunday, so the weekly rhythm is visible without reading dates.
 * @param {string} day Y-m-d.
 * @returns {boolean}
 */
function isWeekend(day) {
  const weekday = new Date(`${day}T00:00:00`).getDay();
  return weekday === 0 || weekday === 6;
}
</script>

<template>
  <div :class="$style.card">
    <div :class="$style.head">
      <div>
        <h3 :class="$style.title">
          Financial breakdown per day
        </h3>
        <p :class="$style.note">
          Cash by the day it was approved. Product-line columns apportion each payment across its
          order's lines, or its registrations when it has no order, by value, so with Unallocated they
          add up to Net. A date opens that day in the payments list.
        </p>
      </div>
      <div :class="$style.measure"
role="group"
aria-label="Show payment methods as"
>
        <span :class="$style.measureLabel">Methods</span>
        <button
          v-for="option in measures"
          :key="option.value"
          type="button"
          :class="[$style.measureButton, measure === option.value ? $style.measureActive : '']"
          :aria-pressed="measure === option.value ? 'true' : 'false'"
          @click="measure = option.value"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <p v-if="days.length === 0" :class="$style.empty">
      No payments approved in this window.
    </p>

    <div v-else :class="[$style.scroller, isLoading ? $style.stale : '']">
      <table :class="$style.table">
        <thead>
          <tr>
            <th scope="col" :class="$style.stickyHead">
              Date
            </th>
            <th scope="col">
              Collected
            </th>
            <th scope="col">
              Refunded
            </th>
            <th scope="col" :class="$style.divider">
              Net
            </th>
            <th v-for="method in methods"
:key="method.key"
scope="col"
>
              {{ method.label }}
            </th>
            <th v-if="hasApplePay"
scope="col"
:class="$style.subtleHead"
>
              of which Apple Pay
            </th>
            <th
              v-for="(line, index) in lines"
              :key="line.key"
              scope="col"
              :class="index === 0 ? $style.divider : ''"
            >
              {{ line.label }}
            </th>
            <th v-if="hasUnallocated"
scope="col"
:class="$style.subtleHead"
>
              Unallocated
            </th>
            <th scope="col">
              Payments
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="row in days"
:key="row.day"
:class="isWeekend(row.day) ? $style.weekend : ''"
>
            <th scope="row" :class="$style.stickyCell">
              <a :href="paymentsUrl(row.day)"
:class="$style.dayLink"
target="_blank"
rel="noopener"
>
                {{ dayLabel(row.day) }}
              </a>
            </th>
            <td>{{ money.format(row.collected) }}</td>
            <td :class="row.refunded ? $style.negative : $style.zero">
              {{ row.refunded ? `-${money.format(row.refunded)}` : '—' }}
            </td>
            <td :class="[$style.divider, $style.strong]">
              {{ money.format(row.net) }}
            </td>
            <td
              v-for="method in methods"
              :key="method.key"
              :class="{
                [$style.zero]: !methodValue(row, method.key),
                [$style.negative]: measure === MEASURE.REFUNDED && methodValue(row, method.key),
              }"
            >
              {{ methodValue(row, method.key) ? compact.format(methodValue(row, method.key)) : '—' }}
            </td>
            <td v-if="hasApplePay" :class="!row.applePay ? $style.zero : $style.subtle">
              {{ row.applePay ? compact.format(row.applePay) : '—' }}
            </td>
            <td
              v-for="(line, index) in lines"
              :key="line.key"
              :class="[index === 0 ? $style.divider : '', !row.lines[line.key] ? $style.zero : '']"
            >
              {{ row.lines[line.key] ? compact.format(row.lines[line.key]) : '—' }}
            </td>
            <td v-if="hasUnallocated" :class="!row.unallocated ? $style.zero : $style.subtle">
              {{ row.unallocated ? compact.format(row.unallocated) : '—' }}
            </td>
            <td :class="$style.zero">
              {{ number.format(row.paymentCount) }}
            </td>
          </tr>
        </tbody>

        <tfoot v-if="totals">
          <tr :class="$style.totalRow">
            <th scope="row" :class="$style.stickyCell">
              Total
            </th>
            <td>{{ money.format(totals.collected) }}</td>
            <td :class="totals.refunded ? $style.negative : ''">
              {{ totals.refunded ? `-${money.format(totals.refunded)}` : money.format(0) }}
            </td>
            <td :class="$style.divider">
              {{ money.format(totals.net) }}
            </td>
            <td v-for="method in methods" :key="method.key">
              {{ compact.format(methodValue(totals, method.key)) }}
            </td>
            <td v-if="hasApplePay">
              {{ compact.format(totals.applePay) }}
            </td>
            <td
              v-for="(line, index) in lines"
              :key="line.key"
              :class="index === 0 ? $style.divider : ''"
            >
              {{ compact.format(totals.lines[line.key] ?? 0) }}
            </td>
            <td v-if="hasUnallocated">
              {{ compact.format(totals.unallocated) }}
            </td>
            <td>{{ number.format(totals.paymentCount) }}</td>
          </tr>
          <tr :class="$style.shareRow">
            <th scope="row" :class="$style.stickyCell">
              Share
            </th>
            <td />
            <td />
            <td :class="$style.divider" />
            <td v-for="method in methods" :key="method.key">
              {{ share(methodValue(totals, method.key), methodTotal) }}
            </td>
            <td v-if="hasApplePay" />
            <td
              v-for="(line, index) in lines"
              :key="line.key"
              :class="index === 0 ? $style.divider : ''"
            >
              {{ share(totals.lines[line.key] ?? 0, totals.net) }}
            </td>
            <td v-if="hasUnallocated">
              {{ share(totals.unallocated, totals.net) }}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>

    <p :class="$style.note">
      The Share row gives each method's part of the methods total, in the measure chosen above, and
      each product line's part of Net.
      Apex site sales are recorded without an order, so they are split by the registrations they
      paid for and appear in the Harassment (Apex site) column, beside the harassment sold through the
      PFS checkout.
      <template v-if="hasUnallocated">
        Unallocated is money that cannot be traced to a product: a payment with no order or
        registration behind it, a refund on one, or an order whose lines are all deleted.
      </template>
    </p>
  </div>
</template>

<style module>
.card {
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 2px var(--shadow-color);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  align-items: flex-start;
}
.title {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.note {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--faint-color);
  max-width: 70ch;
}
.measure {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.measureLabel {
  font-size: var(--font-size-md);
  color: var(--faint-color);
  margin-right: 2px;
}
.measureButton {
  border: 1px solid var(--border-color);
  background: white;
  border-radius: var(--border-radius);
  padding: 4px 10px;
  font: inherit;
  font-size: var(--font-size-md);
  color: var(--primary-font-color);
  cursor: pointer;
}
.measureButton:hover {
  border-color: var(--pfs-red);
}
.measureActive {
  background: var(--pfs-red);
  border-color: var(--pfs-red);
  color: white;
}
.dayLink {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px dotted var(--faint-color);
}
.dayLink:hover {
  color: var(--pfs-red);
  border-bottom-color: var(--pfs-red);
}
.stale {
  opacity: 0.45;
  pointer-events: none;
  transition: opacity 0.15s ease;
}
/* The table is deliberately wide; it scrolls inside its own box so the page never does. */
.scroller {
  overflow-x: auto;
  contain: inline-size;
  max-height: 640px;
  overflow-y: auto;
}
.table {
  border-collapse: collapse;
  font-size: var(--font-size-md);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  min-width: 100%;
}
.table th,
.table td {
  padding: 6px 10px;
  text-align: right;
  border-bottom: 1px solid var(--border-color-light);
}
.table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
  color: var(--faint-color);
  font-weight: 600;
  text-transform: uppercase;
  font-size: var(--font-size-sm);
  letter-spacing: 0.03em;
  border-bottom: 2px solid var(--border-color);
}
.stickyHead {
  left: 0;
  z-index: 3 !important;
  text-align: left !important;
}
.stickyCell {
  position: sticky;
  left: 0;
  background: white;
  text-align: left !important;
  font-weight: 500;
  color: var(--primary-font-dark-color);
  z-index: 1;
}
.weekend .stickyCell,
.weekend td {
  background: var(--table-cell-background-color-darker);
}
.divider {
  border-left: 1px solid var(--border-color);
}
.strong {
  font-weight: 700;
  color: var(--primary-font-dark-color);
}
.negative {
  color: var(--button-red);
}
.zero {
  color: var(--faint-color);
}
.subtle,
.subtleHead {
  color: var(--faint-color);
  font-style: italic;
}
.table {
  --share-row-height: 28px;
}
.table tfoot th,
.table tfoot td {
  position: sticky;
  bottom: 0;
  background: var(--background-light-color);
  font-weight: 700;
  color: var(--primary-font-dark-color);
  border-top: 2px solid var(--border-color);
}
/* Both footer rows stay pinned: the totals sit just above the share row beneath them. */
.table tfoot .totalRow th,
.table tfoot .totalRow td {
  bottom: var(--share-row-height);
}
.table tfoot .shareRow th,
.table tfoot .shareRow td {
  box-sizing: border-box;
  height: var(--share-row-height);
  font-weight: 400;
  color: var(--faint-color);
  border-top: 1px solid var(--border-color-light);
}
.table tfoot .shareRow th {
  font-weight: 500;
}
.empty {
  margin: 0;
  padding: 30px 0;
  text-align: center;
  color: var(--faint-color);
  font-size: var(--font-size-lg);
}
</style>
